import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, CircularProgress, Divider, TextField,
} from '@mui/material';
import { CheckCircle as CheckIcon, Check as TickIcon } from '@mui/icons-material';
import { patientTimeLabel, patientDayLabel, patientBookingSummary } from './publicBookingTime';
import { bookingPlan, initialPicks, PlanAppointment, Picks, goneAdvice, changeAdvice, telHref, Advice } from './publicBookingPlan';
import { heldUntilLabel } from '../components/Dashboard/offerMessage';

// The one page a patient sees, opened from a link with no account and no
// session. Written to be plain rather than clever: most people will open this
// on a phone, from a text, once — and some of them are eighty.
//
// So: large type, one thing to do at a time, full-width buttons far enough
// apart that nothing is tapped by accident, and exactly one button that books.
// What it may book is decided by the server; publicBookingPlan mirrors that
// so the button is never offered for something that would be refused.
//
// It knows almost nothing — a first name, what the appointments are and who
// with, some times. Everything else stays on the other side of the API.

type LinkInfo = {
  patientFirstName: string;
  serviceName: string;
  providerName: string;
  durationMinutes: number;
  expiresAt: string;
  // The times the office held for this patient, flat. Kept for a server that
  // has not been restarted since `appointments` was added.
  offeredSlots?: Array<{ date: string; time: string; duration: number }>;
  // The same times as the appointments to book: one, or — a lab draw and
  // then the doctor — two. Empty means "pick any free time".
  appointments?: PlanAppointment[];
  // The practice's number, when one is set in Admin. Absent means none.
  practicePhone?: string;
};

type Slot = { time: string; duration: number; date?: string };
type Booked = { confirmed: boolean; lines: string[]; texted: boolean };

const API = process.env.REACT_APP_API_URL || 'http://localhost:9000/api';

// Comfortably readable and comfortably tappable. 56px is a thumb.
const BODY = { fontSize: '1.125rem', lineHeight: 1.5 } as const;
const TIME_BUTTON = { minHeight: 56, textTransform: 'none', fontSize: '1.2rem', fontWeight: 600, borderWidth: 2, '&:hover': { borderWidth: 2 } } as const;

// A sentence with the practice's number in it, the number a tappable link.
// With no number set the sentence simply does not mention calling.
const AdviceLine: React.FC<{ advice: Advice }> = ({ advice }) => (
  <Typography sx={{ ...BODY, color: 'text.secondary' }}>
    {advice.before}
    {advice.phone && (
      <a href={telHref(advice.phone)} style={{ color: 'inherit', fontWeight: 700, whiteSpace: 'nowrap' }}>
        {advice.phone}
      </a>
    )}
    {advice.after}
  </Typography>
);

const PublicBooking: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [info, setInfo] = useState<LinkInfo | null>(null);
  const [gone, setGone] = useState(false);
  // A dead link answers with nothing but the practice's number (if set), so
  // the page can still say how to reach the office.
  const [gonePhone, setGonePhone] = useState<string | null>(null);
  const [day, setDay] = useState(() => {
    const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
    return d.toISOString().slice(0, 10);
  });
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState(false);
  // Held times: one pick per appointment. Tapping a time picks it; the one
  // button books. It used to book on the first tap, so a mis-tap on a phone
  // held a real slot in the practice's diary with no way back.
  const [picks, setPicks] = useState<Picks>({});
  // An open link: one time off the grid for the chosen day.
  const [picked, setPicked] = useState<string | null>(null);
  const [booked, setBooked] = useState<Booked | null>(null);
  const [error, setError] = useState('');

  // What this link holds, as appointments. A server that only sends the flat
  // list is read as one appointment.
  const appointments = useMemo<PlanAppointment[]>(() => {
    if (!info) return [];
    if (info.appointments && info.appointments.length) return info.appointments;
    const flat = info.offeredSlots || [];
    if (!flat.length) return [];
    return [{
      step: 1, serviceName: info.serviceName, providerName: info.providerName,
      durationMinutes: info.durationMinutes, slots: flat.map((s) => ({ date: s.date, time: s.time })),
    }];
  }, [info]);
  const isHeld = appointments.length > 0;
  const many = appointments.length > 1;
  const plan = useMemo(() => bookingPlan(appointments, picks), [appointments, picks]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/booking/${token}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setGonePhone(body?.practicePhone || null);
          setGone(true);
          return;
        }
        setInfo((await res.json()).data);
      } catch {
        setGone(true);
      }
    })();
  }, [token]);

  // An appointment with only one time has nothing to choose: it starts chosen.
  useEffect(() => { setPicks(initialPicks(appointments)); }, [appointments]);

  const loadSlots = useCallback(async () => {
    setLoadingSlots(true);
    setError('');
    try {
      const res = await fetch(`${API}/booking/${token}/slots?from=${day}`);
      if (!res.ok) { setGone(true); return; }
      setSlots((await res.json()).data || []);
    } catch {
      setError('We could not load the times just now.');
    } finally {
      setLoadingSlots(false);
    }
  }, [token, day]);

  // Held times come with the link itself; there is nothing to load by day.
  useEffect(() => { if (info && !booked && !isHeld) loadSlots(); }, [info, booked, isHeld, loadSlots]);
  useEffect(() => { setPicked(null); }, [day]);

  const send = async (body: object, onDone: (data: any) => Booked) => {
    setBooking(true);
    setError('');
    try {
      const res = await fetch(`${API}/booking/${token}/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(payload?.error || 'That time is no longer available.');
        if (!isHeld) loadSlots();
        return;
      }
      setBooked(onDone(payload?.data || {}));
      setPicked(null);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setBooking(false);
    }
  };

  const bookHeld = () => {
    if (!plan.ready) return;
    // The lines the patient just read are the lines they are thanked with.
    const lines = plan.summary;
    send({ picks: plan.picks }, (data) => ({ confirmed: data?.status === 'confirmed', lines, texted: Boolean(data?.confirmationSent) }));
  };

  const bookOpen = () => {
    if (!picked || !info) return;
    const line = patientBookingSummary(day, picked, info.durationMinutes, info.providerName);
    // The server says which it was: a booking the office had already chosen
    // the time for, or a request they still have to look at.
    send({ date: day, time: picked }, (data) => ({ confirmed: data?.status === 'confirmed', lines: [line], texted: Boolean(data?.confirmationSent) }));
  };

  const shell = (children: React.ReactNode) => (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', p: { xs: 2, sm: 4 }, display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '100%', maxWidth: 520 }}>{children}</Box>
    </Box>
  );

  // One message for every reason a link will not work — expired, already used,
  // withdrawn, never existed. The patient cannot act on the difference, and
  // spelling it out would tell anyone probing which tokens are real.
  if (gone) {
    return shell(
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 700 }}>This link is no longer available</Typography>
        <AdviceLine advice={goneAdvice(gonePhone || info?.practicePhone)} />
      </Paper>,
    );
  }

  if (!info) {
    return shell(<Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>);
  }

  if (booked) {
    return shell(
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
          <CheckIcon color="success" sx={{ fontSize: 36 }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Thank you, {info.patientFirstName}</Typography>
        </Box>
        <Typography sx={{ ...BODY, mb: 1 }}>
          {booked.confirmed
            ? (booked.lines.length > 1 ? 'You are booked for:' : 'You are booked for')
            : 'We have you down for'}
        </Typography>
        <Box component="ul" sx={{ m: 0, mb: 2, pl: booked.lines.length > 1 ? 3 : 0, listStyle: booked.lines.length > 1 ? 'disc' : 'none' }}>
          {booked.lines.map((line) => (
            <Typography key={line} component="li" sx={{ ...BODY, fontWeight: 700, mb: 0.5 }}>{line}</Typography>
          ))}
        </Box>
        {/* Said plainly either way. A time the office chose and held is booked
            the moment it is taken; a time picked off the open grid is a
            request until a person looks at it. */}
        <AdviceLine advice={changeAdvice(booked.confirmed, info.practicePhone, booked.texted)} />
      </Paper>,
    );
  }

  const heldUntil = heldUntilLabel(info.expiresAt, Intl.DateTimeFormat().resolvedOptions().timeZone);

  return shell(
    <>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>Hi {info.patientFirstName}</Typography>
        <Typography sx={{ ...BODY }}>
          {isHeld ? plan.intro : 'Choose a time that suits you and we will confirm it.'}
        </Typography>
        {!isHeld && (
          <Typography sx={{ ...BODY, mt: 1.5, fontWeight: 700 }}>
            {info.serviceName} with {info.providerName} · {info.durationMinutes} minutes
          </Typography>
        )}
        {heldUntil && (
          <Typography sx={{ mt: 1.5, fontSize: '1rem', color: 'text.secondary' }}>
            {isHeld ? `Held for you until ${heldUntil}.` : `This link works until ${heldUntil}.`}
          </Typography>
        )}
      </Paper>

      {/* Held times: no day picker and no grid — only what the office offered.
          Each appointment is its own card: what it is, who with, how long,
          the day in words, and its times one under another so nothing sits
          close enough to a tap to be fat-fingered. */}
      {isHeld && appointments.map((a) => {
        const days = Array.from(new Set(a.slots.map((s) => s.date)));
        const chosen = picks[a.step];
        return (
          <Paper key={a.step} sx={{ p: 3, mb: 2 }} aria-label={`Appointment ${a.step}`}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
              {many && (
                <Box
                  aria-hidden
                  sx={{
                    flexShrink: 0, width: 36, height: 36, borderRadius: '50%',
                    bgcolor: chosen ? 'success.main' : 'primary.main', color: 'common.white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: '1.1rem',
                  }}
                >
                  {chosen ? <TickIcon fontSize="small" /> : a.step}
                </Box>
              )}
              <Box sx={{ minWidth: 0 }}>
                {many && (
                  <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary', letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 600 }}>
                    Appointment {a.step} of {appointments.length}
                  </Typography>
                )}
                <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, lineHeight: 1.25 }}>{a.serviceName}</Typography>
                <Typography sx={{ ...BODY, color: 'text.secondary' }}>
                  with {a.providerName} · {a.durationMinutes} minutes
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {days.map((d) => (
                <Box key={d}>
                  <Typography sx={{ ...BODY, fontWeight: 700, mb: 1 }}>{patientDayLabel(d)}</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {a.slots.filter((s) => s.date === d).map((slot) => {
                      const isChosen = chosen?.date === slot.date && chosen?.time === slot.time;
                      return (
                        <Button
                          key={`${slot.date} ${slot.time}`}
                          variant={isChosen ? 'contained' : 'outlined'}
                          disabled={booking}
                          aria-pressed={isChosen}
                          startIcon={isChosen ? <TickIcon /> : undefined}
                          onClick={() => setPicks((prev) => ({ ...prev, [a.step]: slot }))}
                          sx={TIME_BUTTON}
                        >
                          {patientTimeLabel(slot.time)}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        );
      })}

      {/* One button, and above it exactly what it will book. Not offered
          until every appointment has a time; what is missing is said. */}
      {isHeld && (
        <Paper sx={{ p: 3 }}>
          {plan.summary.length > 0 && (
            <>
              <Typography sx={{ fontSize: '1rem', color: 'text.secondary', mb: 0.5 }}>You are booking</Typography>
              <Box component="ul" sx={{ m: 0, mb: 2, pl: many ? 3 : 0, listStyle: many ? 'disc' : 'none' }}>
                {plan.summary.map((line) => (
                  <Typography key={line} component="li" sx={{ ...BODY, fontWeight: 700, mb: 0.5 }}>{line}</Typography>
                ))}
              </Box>
            </>
          )}
          {plan.blocker && (
            <Typography sx={{ ...BODY, mb: 2 }} role="status">{plan.blocker}</Typography>
          )}
          <Button
            variant="contained"
            fullWidth
            onClick={bookHeld}
            disabled={!plan.ready || booking}
            sx={{ minHeight: 60, textTransform: 'none', fontSize: '1.25rem', fontWeight: 700 }}
          >
            {booking ? 'Booking…' : plan.button}
          </Button>
          {error && (
            <Typography sx={{ ...BODY, mt: 2 }} color="error" role="alert">{error}</Typography>
          )}
          <Typography sx={{ mt: 2, fontSize: '1rem', color: 'text.secondary' }}>
            Need a different time? Just reply to our text.
          </Typography>
        </Paper>
      )}

      {/* An open link: the patient picks any free time on a day they choose. */}
      {!isHeld && (
        <Paper sx={{ p: 3 }}>
          <TextField
            label="Day"
            type="date"
            value={day}
            onChange={(e) => setDay(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2, '& input': { fontSize: '1.125rem' } }}
          />
          <Divider sx={{ mb: 2 }} />

          {loadingSlots ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
          ) : slots.length === 0 ? (
            <Typography sx={{ ...BODY, color: 'text.secondary' }}>
              Nothing free that day. Try another one.
            </Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 1.25 }}>
              {slots.map((slot) => (
                <Button
                  key={slot.time}
                  variant={picked === slot.time ? 'contained' : 'outlined'}
                  disabled={booking}
                  aria-pressed={picked === slot.time}
                  onClick={() => setPicked(slot.time)}
                  sx={TIME_BUTTON}
                >
                  {patientTimeLabel(slot.time)}
                </Button>
              ))}
            </Box>
          )}

          {/* Below the times, not over them: the other options stay visible, so
              changing your mind is tapping a different time rather than
              dismissing something first. */}
          {picked && (
            <Box sx={{ mt: 2.5, p: 2, borderRadius: 1, bgcolor: 'grey.100' }}>
              <Typography sx={{ fontSize: '1rem', color: 'text.secondary', mb: 0.5 }}>You are asking for</Typography>
              <Typography sx={{ ...BODY, fontWeight: 700, mb: 2 }}>
                {patientBookingSummary(day, picked, info.durationMinutes, info.providerName)}
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={bookOpen}
                disabled={booking}
                sx={{ minHeight: 60, textTransform: 'none', fontSize: '1.25rem', fontWeight: 700 }}
              >
                {booking ? 'Sending…' : 'Ask for this time'}
              </Button>
            </Box>
          )}

          {error && (
            <Typography sx={{ ...BODY, mt: 2 }} color="error" role="alert">{error}</Typography>
          )}
        </Paper>
      )}
    </>,
  );
};

export default PublicBooking;
